import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { 
  Section, 
  Card, 
  Button,
  FadeInUp,
  SlideIn,
  StaggerContainer,
  StaggerItem,
} from '../../components/common';
import { 
  DESTINATIONS, 
  TEACHER_PHOTOS, 
  ABOUT_GALLERY,
  HOME_DESTINATIONS_GRID,
} from '../../constants';
import { usePageTitle } from '../../hooks/usePageTitle';

// Fix para ícones do Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Ícone personalizado
const customIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Dados dos professores (usa TEACHER_PHOTOS de constants/images.ts para as imagens)
const TEACHERS = [
  {
    name: 'Elienai Santos',
    role: 'Professora e fundadora',
    image: TEACHER_PHOTOS[0].url,
    countries: ['Irlanda', 'Escócia', 'Brasil'],
    bio: 'Morou dois anos na Irlanda, onde trabalhou e aperfeiçoou métodos de ensino. É graduada em Letras com pós-graduação em Linguística Aplicada e especialização em exames Cambridge na Escócia.',
    certifications: ['Cambridge CAE', 'IELTS 8.5'],
  },
  {
    name: 'Roger Santana',
    role: 'Professor',
    image: TEACHER_PHOTOS[1].url,
    countries: ['Estados Unidos', 'Inglaterra', 'Irlanda'],
    bio: 'Viajante com mais de 55 países visitados e residente na Irlanda há 13 anos. Ator formado em Artes Cênicas, usa referências de arte, poesia e música para tornar as aulas dinâmicas e práticas.',
    certifications: ['CELTA', 'TOEFL iBT 118'],
  },
  {
    name: 'Tallyne',
    role: 'Professora',
    image: TEACHER_PHOTOS[2].url,
    countries: ['Estados Unidos', 'Canadá'],
    bio: 'Já viveu em cinco países e atualmente mora na Irlanda. Recifense, formado em Filosofia, é apaixonado por escrita e por conversas profundas. Adora discutir livros e viagens e está criando uma criança bilíngue.',
    certifications: ['TESOL', 'Michigan ECPE'],
  },
];

// Fotos da galeria (usa ABOUT_GALLERY de constants/images.ts)
const GALLERY_PHOTOS = ABOUT_GALLERY.map(img => ({
  url: img.url,
  caption: img.caption,
}));

// Coordenadas dos destinos para o mapa com aprendizados específicos
const MAP_MARKERS = [
  { 
    city: 'Berlin', 
    country: 'Alemanha', 
    lat: 52.5200, 
    lng: 13.4050,
    learning: 'Em Berlim, aprendi a importância da pronúncia precisa ao alternar entre inglês e alemão em ambientes profissionais.',
    landmark: 'Portão de Brandemburgo, Muro de Berlim, Reichstag'
  },
  { 
    city: 'Vienna', 
    country: 'Áustria', 
    lat: 48.2082, 
    lng: 16.3738,
    learning: 'Viena me ensinou o inglês formal europeu, essencial para negociações e apresentações corporativas.',
    landmark: 'Palácio de Schönbrunn, Ópera, Prater'
  },
  { 
    city: 'Brussels', 
    country: 'Bélgica', 
    lat: 50.8503, 
    lng: 4.3517,
    learning: 'No coração da União Europeia, desenvolvi habilidades de comunicação intercultural em inglês.',
    landmark: 'Grand Place, Atomium, Manneken Pis'
  },
  { 
    city: 'Sofia', 
    country: 'Bulgária', 
    lat: 42.6977, 
    lng: 23.3219,
    learning: 'Sofia me mostrou como adaptar minha comunicação em inglês para diferentes níveis de fluência.',
    landmark: 'Catedral Alexander Nevsky, Monte Vitosha'
  },
  { 
    city: 'Zagreb', 
    country: 'Croácia', 
    lat: 45.8150, 
    lng: 15.9819,
    learning: 'Aprendi a valorizar a comunicação clara e direta, essencial nos Balcãs.',
    landmark: 'Ban Jelačić Square, Catedral, Mercado Dolac'
  },
  { 
    city: 'Copenhagen', 
    country: 'Dinamarca', 
    lat: 55.6761, 
    lng: 12.5683,
    learning: 'Na Dinamarca, observei como a fluência natural em inglês facilita ambientes de trabalho colaborativos.',
    landmark: 'Nyhavn, A Pequena Sereia, Tivoli Gardens'
  },
  { 
    city: 'Ljubljana', 
    country: 'Eslovênia', 
    lat: 46.0569, 
    lng: 14.5058,
    learning: 'Ljubljana me ensinou que até pequenos países têm riqueza linguística impressionante.',
    landmark: 'Castelo de Ljubljana, Ponte Tripla, Lago Bled'
  },
  { 
    city: 'Barcelona', 
    country: 'Espanha', 
    lat: 41.3851, 
    lng: 2.1734,
    learning: 'Descobri como navegar entre três idiomas no ambiente profissional barcelonês.',
    landmark: 'Sagrada Família, Park Güell, Las Ramblas'
  },
  { 
    city: 'Paris', 
    country: 'França', 
    lat: 48.8566, 
    lng: 2.3522,
    learning: 'Paris me desafiou a manter confiança ao falar inglês, mesmo em um país orgulhoso de sua língua.',
    landmark: 'Torre Eiffel, Louvre, Arco do Triunfo'
  },
  { 
    city: 'Athens', 
    country: 'Grécia', 
    lat: 37.9838, 
    lng: 23.7275,
    learning: 'Em Atenas, aprendi que hospitalidade e comunicação clara andam juntas.',
    landmark: 'Acrópole, Partenon, Plaka'
  },
  { 
    city: 'Budapest', 
    country: 'Hungria', 
    lat: 47.4979, 
    lng: 19.0402,
    learning: 'Budapeste me mostrou como o inglês é ponte essencial no leste europeu.',
    landmark: 'Parlamento, Termas Széchenyi, Bastião dos Pescadores'
  },
  { 
    city: 'Dublin', 
    country: 'Irlanda', 
    lat: 53.3498, 
    lng: -6.2603,
    learning: 'Em Dublin, mergulhei nas expressões idiomáticas irlandesas que enriquecem o inglês cotidiano.',
    landmark: 'Temple Bar, Trinity College, Guinness Storehouse'
  },
  { 
    city: 'Belfast', 
    country: 'Irlanda do Norte', 
    lat: 54.5973, 
    lng: -5.9301,
    learning: 'Belfast me ensinou sobre diferentes sotaques britânicos e compreensão auditiva avançada.',
    landmark: 'Titanic Belfast, Giant\'s Causeway, City Hall'
  },
  { 
    city: 'Edinburgh', 
    country: 'Escócia', 
    lat: 55.9533, 
    lng: -3.1883,
    learning: 'O sotaque escocês me desafiou e aprimorou minha capacidade de compreensão oral.',
    landmark: 'Castelo de Edimburgo, Royal Mile, Arthur\'s Seat'
  },
  { 
    city: 'London', 
    country: 'Inglaterra', 
    lat: 51.5074, 
    lng: -0.1278,
    learning: 'Londres foi minha escola de British English, onde aprendi a comunicação profissional em empresas globais.',
    landmark: 'Big Ben, Tower Bridge, Camden Town'
  },
  { 
    city: 'Rome', 
    country: 'Itália', 
    lat: 41.9028, 
    lng: 12.4964,
    learning: 'Roma me ensinou que comunicação eficaz vai além das palavras — inclui gestos e expressão.',
    landmark: 'Coliseu, Vaticano, Fontana di Trevi'
  },
  { 
    city: 'Luxembourg', 
    country: 'Luxemburgo', 
    lat: 49.6116, 
    lng: 6.1319,
    learning: 'Luxemburgo demonstrou como o inglês facilita negócios em ambientes multilíngues.',
    landmark: 'Casemates, Palácio Grão-Ducal, Kirchberg'
  },
  { 
    city: 'Valletta', 
    country: 'Malta', 
    lat: 35.8989, 
    lng: 14.5146,
    learning: 'Malta, onde o inglês é oficial, me mostrou variações mediterrâneas fascinantes do idioma.',
    landmark: 'Upper Barrakka Gardens, Blue Lagoon, Mdina'
  },
  { 
    city: 'Amsterdam', 
    country: 'Holanda', 
    lat: 52.3676, 
    lng: 4.9041,
    learning: 'Os holandeses me inspiraram: seu domínio perfeito do inglês é resultado de imersão constante.',
    landmark: 'Canais, Museu Van Gogh, Anne Frank House'
  },
  { 
    city: 'Krakow', 
    country: 'Polônia', 
    lat: 50.0647, 
    lng: 19.9450,
    learning: 'Cracóvia me ensinou paciência e adaptabilidade ao ensinar inglês para falantes de línguas eslavas.',
    landmark: 'Praça do Mercado, Castelo Wawel, Kazimierz'
  },
  { 
    city: 'Lisbon', 
    country: 'Portugal', 
    lat: 38.7223, 
    lng: -9.1393,
    learning: 'Lisboa me mostrou a importância da empatia ao ensinar — cada aluno tem seu próprio ritmo.',
    landmark: 'Torre de Belém, Alfama, Elétrico 28'
  },
  { 
    city: 'Prague', 
    country: 'República Tcheca', 
    lat: 50.0755, 
    lng: 14.4378,
    learning: 'Praga me ensinou que ambientes descontraídos podem ser excelentes para praticar conversação.',
    landmark: 'Ponte Carlos, Castelo, Relógio Astronômico'
  },
  { 
    city: 'Stockholm', 
    country: 'Suécia', 
    lat: 59.3293, 
    lng: 18.0686,
    learning: 'Estocolmo exemplifica como educação de qualidade gera fluência excepcional em inglês.',
    landmark: 'Gamla Stan, Museu Vasa, Palácio Real'
  },
  { 
    city: 'Zurich', 
    country: 'Suíça', 
    lat: 47.3769, 
    lng: 8.5417,
    learning: 'Zurique demonstrou a importância do inglês como língua franca em ambientes corporativos internacionais.',
    landmark: 'Lago de Zurique, Bahnhofstrasse, Alpes'
  },
  { 
    city: 'Monaco', 
    country: 'Mônaco', 
    lat: 43.7384, 
    lng: 7.4246,
    learning: 'Mônaco me ensinou o inglês de negócios de alto nível, essencial para networking internacional.',
    landmark: 'Cassino de Monte Carlo, Porto, Palácio do Príncipe'
  },
  { 
    city: 'Reykjavik', 
    country: 'Islândia', 
    lat: 64.1466, 
    lng: -21.9426,
    learning: 'A Islândia me mostrou como uma nação pequena pode ter proficiência exemplar em inglês.',
    landmark: 'Hallgrímskirkja, Blue Lagoon, Golden Circle'
  },
  { 
    city: 'Marrakech', 
    country: 'Marrocos', 
    lat: 31.6295, 
    lng: -7.9811,
    learning: 'Marrakech me desafiou a usar inglês em contextos multilíngues complexos (árabe, francês, berbere).',
    landmark: 'Jemaa el-Fna, Jardim Majorelle, Medina'
  },
  { 
    city: 'Cairo', 
    country: 'Egito', 
    lat: 30.0444, 
    lng: 31.2357,
    learning: 'No Cairo, aprendi a importância da clareza na comunicação em ambientes de alta diversidade cultural.',
    landmark: 'Pirâmides de Gizé, Museu Egípcio, Khan el-Khalili'
  },
  { 
    city: 'Amman', 
    country: 'Jordânia', 
    lat: 31.9454, 
    lng: 35.9284,
    learning: 'Amã me ensinou que o inglês abre portas mesmo em regiões onde não é língua principal.',
    landmark: 'Petra, Mar Morto, Wadi Rum'
  },
  { 
    city: 'Lima', 
    country: 'Peru', 
    lat: -12.0464, 
    lng: -77.0428,
    learning: 'Lima me mostrou como o inglês facilita conexões na crescente cena de startups latino-americanas.',
    landmark: 'Plaza de Armas, Miraflores, Cusco'
  },
  { 
    city: 'Asunción', 
    country: 'Paraguai', 
    lat: -25.2637, 
    lng: -57.5759,
    learning: 'Assunção me ensinou a valorizar cada oportunidade de prática, mesmo em lugares menos anglófonos.',
    landmark: 'Palácio de López, Costanera, Itaipu'
  },
  { 
    city: 'Bogotá', 
    country: 'Colômbia', 
    lat: 4.7110, 
    lng: -74.0721,
    learning: 'Bogotá me mostrou o boom do inglês na América Latina e sua importância para o mercado de trabalho.',
    landmark: 'Candelaria, Monserrate, Museu do Ouro'
  },
  { 
    city: 'La Paz', 
    country: 'Bolívia', 
    lat: -16.5000, 
    lng: -68.1500,
    learning: 'La Paz me ensinou que altitude não é barreira para aprender — determinação é o que importa.',
    landmark: 'Valle de la Luna, Teleférico, Mercado das Bruxas'
  },
  { 
    city: 'Buenos Aires', 
    country: 'Argentina', 
    lat: -34.6037, 
    lng: -58.3816,
    learning: 'Buenos Aires me ensinou sobre sotaques e variações regionais do inglês sul-americano.',
    landmark: 'Obelisco, La Boca, Recoleta'
  },
  { 
    city: 'São Paulo', 
    country: 'Brasil', 
    lat: -23.5505, 
    lng: -46.6333,
    learning: 'São Paulo é minha base — onde transformo experiências internacionais em métodos de ensino eficazes.',
    landmark: 'Avenida Paulista, Ibirapuera, MASP'
  },
  { 
    city: 'New York', 
    country: 'Estados Unidos', 
    lat: 40.7128, 
    lng: -74.0060,
    learning: 'Nova York me ensinou inglês americano real — expressões de rua, linguagem corporativa e networking eficaz.',
    landmark: 'Times Square, Central Park, Brooklyn Bridge'
  },
  { 
    city: 'Toronto', 
    country: 'Canadá', 
    lat: 43.6532, 
    lng: -79.3832,
    learning: 'Toronto demonstrou como multiculturalismo e proficiência em inglês criam ambientes de trabalho inclusivos.',
    landmark: 'CN Tower, Distillery District, Niagara Falls'
  },
  { 
    city: 'Istanbul', 
    country: 'Turquia', 
    lat: 41.0082, 
    lng: 28.9784,
    learning: 'Istambul me ensinou que o inglês conecta continentes e culturas — literalmente entre Europa e Ásia.',
    landmark: 'Hagia Sophia, Grand Bazaar, Bósforo'
  },
];

// Componente para centralizar o mapa quando um marcador é selecionado
function FlyToMarker({ position }: { position: [number, number] | null }) {
  const map = useMap();
  
  useEffect(() => {
    if (position) {
      map.flyTo(position, 12, { duration: 1.5 });
    }
  }, [position, map]);
  
  return null;
}

export default function About() {
  usePageTitle('Nossa Jornada');
  const [selectedPhoto, setSelectedPhoto] = useState<number | null>(null);
  const [activeMarker, setActiveMarker] = useState<number | null>(null);

  return (
    <>
      {/* Hero Melhorado */}
      <section className="relative min-h-[55vh] sm:min-h-[60vh] md:min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1488085061387-422e29b40080?w=1920)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-earth-900/80 via-earth-900/60 to-earth-900/80" />
        
        <div className="container-custom relative z-10 pt-20 sm:pt-24 pb-10 sm:pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-12 items-center">
            <FadeInUp>
              <p className="text-olive-400 font-medium mb-2 sm:mb-4 flex items-center gap-2 text-xs sm:text-base">
                <i className="ri-compass-3-line"></i>
                Conheça nossa história
              </p>
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-sand-100 mb-3 sm:mb-6 leading-tight">
                Professores que <span className="text-olive-400">viveram</span> o que ensinam
              </h1>
              <p className="text-sm sm:text-base md:text-lg text-sand-300 leading-relaxed mb-5 sm:mb-8">
                Não somos apenas professores de inglês — somos viajantes, exploradores e 
                apaixonados por culturas. Cada aula traz histórias reais de quem viveu 
                a língua no dia a dia.
              </p>
              <div className="grid grid-cols-3 gap-3 sm:gap-6">
                <div className="text-center">
                  <p className="text-xl sm:text-2xl md:text-3xl font-bold text-olive-400">40+</p>
                  <p className="text-sand-400 text-[10px] sm:text-xs md:text-sm">Países visitados</p>
                </div>
                <div className="text-center border-x border-sand-600 px-1 sm:px-2">
                  <p className="text-xl sm:text-2xl md:text-3xl font-bold text-olive-400">5+</p>
                  <p className="text-sand-400 text-[10px] sm:text-xs md:text-sm">Anos no exterior</p>
                </div>
                <div className="text-center">
                  <p className="text-xl sm:text-2xl md:text-3xl font-bold text-olive-400">500+</p>
                  <p className="text-sand-400 text-[10px] sm:text-xs md:text-sm">Alunos formados</p>
                </div>
              </div>
            </FadeInUp>
            
            <SlideIn direction="right" className="hidden lg:block">
              <div className="grid grid-cols-2 gap-4">
                <img 
                  src={HOME_DESTINATIONS_GRID[0].url} 
                  alt={HOME_DESTINATIONS_GRID[0].alt} 
                  className="rounded-xl shadow-lg w-full h-40 object-cover"
                />
                <img 
                  src={HOME_DESTINATIONS_GRID[1].url} 
                  alt={HOME_DESTINATIONS_GRID[1].alt} 
                  className="rounded-xl shadow-lg w-full h-40 object-cover mt-8"
                />
                <img 
                  src={HOME_DESTINATIONS_GRID[2].url} 
                  alt={HOME_DESTINATIONS_GRID[2].alt} 
                  className="rounded-xl shadow-lg w-full h-40 object-cover -mt-4"
                />
                <img 
                  src={HOME_DESTINATIONS_GRID[4].url} 
                  alt={HOME_DESTINATIONS_GRID[4].alt} 
                  className="rounded-xl shadow-lg w-full h-40 object-cover mt-4"
                />
              </div>
            </SlideIn>
          </div>
        </div>
      </section>

      {/* Professores */}
      <Section bgColor="sand">
        <FadeInUp className="text-center mb-8 sm:mb-12">
          <p className="text-olive-600 font-medium mb-2 flex items-center justify-center gap-2 text-sm">
            <i className="ri-team-line"></i>
            Nossa equipe
          </p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-earth-800 mb-3 sm:mb-4">
            Conheça nossos professores
          </h2>
          <p className="text-earth-600 max-w-2xl mx-auto text-sm sm:text-base">
            Cada professor traz uma bagagem única de experiências internacionais, 
            certificações e paixão por ensinar.
          </p>
        </FadeInUp>

        <StaggerContainer className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6 md:gap-8">
          {TEACHERS.map((teacher) => (
            <StaggerItem key={teacher.name}>
              <Card className="h-full overflow-hidden group">
                <div className="relative mb-3 sm:mb-4">
                  <img 
                    src={teacher.image} 
                    alt={teacher.name}
                    className="w-full h-48 sm:h-56 md:h-64 object-cover rounded-lg group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <h3 className="text-lg sm:text-xl font-semibold text-earth-800">{teacher.name}</h3>
                <p className="text-olive-600 text-xs sm:text-sm font-medium mb-2 sm:mb-3">{teacher.role}</p>
                <p className="text-earth-600 text-xs sm:text-sm leading-relaxed">{teacher.bio}</p>
              </Card>
            </StaggerItem>
          ))}
        </StaggerContainer>
      </Section>

      {/* Mapa Interativo Real */}
      <Section bgColor="white">
        <FadeInUp className="text-center mb-8 sm:mb-12">
          <p className="text-olive-600 font-medium mb-2 flex items-center justify-center gap-2 text-sm">
            <i className="ri-earth-line"></i>
            Nosso mapa
          </p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-earth-800 mb-3 sm:mb-4">
            Por onde já passamos
          </h2>
          <p className="text-earth-600 max-w-2xl mx-auto text-sm sm:text-base">
            Clique nos marcadores para descobrir o que aprendemos em cada destino.
          </p>
        </FadeInUp>

        <div className="relative bg-sand-100 rounded-2xl overflow-hidden shadow-lg">
          {/* Mapa Leaflet Real */}
          <div className="relative h-[350px] sm:h-[450px] md:h-[500px]">
            <MapContainer
              center={[20, 0]}
              zoom={2}
              scrollWheelZoom={true}
              className="h-full w-full rounded-t-2xl"
              style={{ background: '#e8ebe0' }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <FlyToMarker 
                position={activeMarker !== null ? [MAP_MARKERS[activeMarker].lat, MAP_MARKERS[activeMarker].lng] : null} 
              />
              {MAP_MARKERS.map((marker, index) => (
                <Marker 
                  key={marker.city}
                  position={[marker.lat, marker.lng]}
                  icon={customIcon}
                  eventHandlers={{
                    click: () => setActiveMarker(index),
                  }}
                >
                  <Popup>
                    <div className="min-w-[200px]">
                      <h4 className="font-semibold text-earth-800 text-base mb-1">
                        {marker.city}, {marker.country}
                      </h4>
                      <p className="text-olive-600 text-xs mb-2 flex items-center gap-1">
                        <i className="ri-map-pin-line"></i>
                        {marker.landmark}
                      </p>
                      <p className="text-earth-600 text-sm italic">
                        "{marker.learning}"
                      </p>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>
          
          {/* Card de informações do marcador ativo */}
          <AnimatePresence>
            {activeMarker !== null && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                className="absolute bottom-20 sm:bottom-24 left-2 right-2 sm:left-4 sm:right-4 md:left-4 md:right-auto md:w-96 bg-white rounded-xl shadow-lg p-4 sm:p-5 border border-sand-200 z-[1000]"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-olive-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-xl text-olive-600"></i>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-earth-800 text-lg">
                      {MAP_MARKERS[activeMarker].city}
                    </h4>
                    <p className="text-olive-600 text-sm font-medium">
                      {MAP_MARKERS[activeMarker].country}
                    </p>
                    <p className="text-earth-500 text-xs mt-1 flex items-center gap-1">
                      <i className="ri-building-line"></i>
                      {MAP_MARKERS[activeMarker].landmark}
                    </p>
                    <p className="text-earth-700 text-sm mt-3 italic leading-relaxed">
                      "{MAP_MARKERS[activeMarker].learning}"
                    </p>
                  </div>
                  <button 
                    onClick={() => setActiveMarker(null)}
                    className="text-earth-400 hover:text-earth-600 transition-colors"
                  >
                    <i className="ri-close-line text-lg"></i>
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          
          {/* Lista de destinos abaixo do mapa */}
          <div className="p-4 sm:p-6 bg-white border-t border-sand-200">
            <div className="flex overflow-x-auto sm:flex-wrap sm:justify-center gap-2 sm:gap-3 pb-2 sm:pb-0 -mx-2 px-2 sm:mx-0 sm:px-0">
              {MAP_MARKERS.map((marker, index) => (
                <button
                  key={marker.city}
                  onClick={() => setActiveMarker(index)}
                  className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-all whitespace-nowrap flex-shrink-0 ${
                    activeMarker === index 
                      ? 'bg-olive-600 text-white shadow-md' 
                      : 'bg-sand-100 text-earth-700 hover:bg-sand-200'
                  }`}
                >
                  <i className="ri-map-pin-2-line"></i>
                  {marker.city}
                </button>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* Galeria de Fotos */}
      <Section bgColor="sand">
        <FadeInUp className="text-center mb-12">
          <p className="text-olive-600 font-medium mb-2 flex items-center justify-center gap-2">
            <i className="ri-camera-line"></i>
            Galeria
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-earth-800 mb-4">
            Momentos das nossas aulas
          </h2>
          <p className="text-earth-600 max-w-2xl mx-auto">
            Veja como são nossas aulas na prática — em cafés, parques, e ambientes 
            que simulam situações reais do dia a dia.
          </p>
        </FadeInUp>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {GALLERY_PHOTOS.map((photo, index) => (
            <motion.div
              key={index}
              className={`relative overflow-hidden rounded-xl cursor-pointer group ${
                index === 0 || index === 5 ? 'md:row-span-2' : ''
              }`}
              whileHover={{ scale: 1.02 }}
              onClick={() => setSelectedPhoto(index)}
            >
              <img 
                src={photo.url} 
                alt={photo.caption}
                className={`w-full object-cover transition-transform duration-500 group-hover:scale-110 ${
                  index === 0 || index === 5 ? 'h-64 md:h-full' : 'h-48 md:h-56'
                }`}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-earth-900/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="absolute bottom-0 left-0 right-0 p-4 text-white transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                <p className="text-sm font-medium">{photo.caption}</p>
              </div>
              <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                  <i className="ri-zoom-in-line text-white"></i>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Modal da foto */}
        <AnimatePresence>
          {selectedPhoto !== null && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-earth-900/95 flex items-center justify-center p-4"
              onClick={() => setSelectedPhoto(null)}
            >
              <motion.div
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.8 }}
                className="relative max-w-4xl w-full"
                onClick={(e) => e.stopPropagation()}
              >
                <img 
                  src={GALLERY_PHOTOS[selectedPhoto].url.replace('w=600', 'w=1200')} 
                  alt={GALLERY_PHOTOS[selectedPhoto].caption}
                  className="w-full rounded-xl"
                />
                <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-earth-900/90 to-transparent rounded-b-xl">
                  <p className="text-white text-lg font-medium">
                    {GALLERY_PHOTOS[selectedPhoto].caption}
                  </p>
                </div>
                <button 
                  onClick={() => setSelectedPhoto(null)}
                  className="absolute top-4 right-4 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
                
                {/* Navegação */}
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedPhoto(selectedPhoto > 0 ? selectedPhoto - 1 : GALLERY_PHOTOS.length - 1);
                  }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                >
                  <i className="ri-arrow-left-s-line text-xl"></i>
                </button>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedPhoto(selectedPhoto < GALLERY_PHOTOS.length - 1 ? selectedPhoto + 1 : 0);
                  }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                >
                  <i className="ri-arrow-right-s-line text-xl"></i>
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </Section>

      {/* Story */}
      <Section bgColor="white">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <SlideIn direction="left">
            <p className="text-olive-600 font-medium mb-2 flex items-center gap-2">
              <i className="ri-book-open-line"></i>
              Como tudo começou
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-earth-800 mb-6">
              Uma paixão por explorar
            </h2>
            <p className="text-earth-600 mb-4 leading-relaxed">
              A Beyond Class nasceu do encontro entre o mundo e o sonho. Da experiência profunda de viver em outros países e da vontade sincera de levar essa vivência para cada aluno, transformando o inglês em algo vivo, humano e possível.
            </p>
            <p className="text-earth-600 mb-4 leading-relaxed">
              Acreditamos que aprender inglês é abrir portas, atravessar fronteiras e permitir-se viver o novo. É enxergar o idioma como um caminho — para histórias que ainda não foram vividas, oportunidades que ainda não chegaram e sonhos que merecem sair do papel.
            </p>
            <p className="text-earth-600 leading-relaxed">
              Cada aula carrega um pedaço do mundo: as trocas, os encontros, as culturas que se conectam. Aqui, o inglês acontece na vida real. Caminhamos ao lado do aluno na construção de pontes, com a certeza de que sempre é possível ir mais longe, confiar no processo e viver o idioma com significado.
            </p>
          </SlideIn>
          
          <SlideIn direction="right">
            <div className="bg-sand-50 rounded-xl p-8 shadow-sm border border-sand-200">
              <h3 className="text-xl font-semibold text-earth-800 mb-6 flex items-center gap-2">
                <i className="ri-trophy-line text-olive-600"></i>
                Números que importam
              </h3>
              <div className="grid grid-cols-2 gap-6">
                {[
                  { number: '500+', label: 'Alunos formados' },
                  { number: '15+', label: 'Anos de experiência' },
                  { number: '20.000+', label: 'Horas de conversação guiada' },
                  { number: '40+', label: 'Países vividos' },
                ].map((stat) => (
                  <div key={stat.label} className="text-center p-4 bg-white rounded-lg">
                    <p className="text-3xl font-bold text-olive-600">{stat.number}</p>
                    <p className="text-earth-600 text-sm mt-1">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </SlideIn>
        </div>
      </Section>

      {/* Timeline */}
      <Section bgColor="olive">
        <FadeInUp className="text-center mb-12">
          <p className="text-olive-700 font-medium mb-2">Nossa trajetória</p>
          <h2 className="text-3xl md:text-4xl font-bold text-earth-800">
            O caminho até aqui
          </h2>
        </FadeInUp>

        <div className="max-w-3xl mx-auto">
            {[
            { 
              year: '2010', 
              title: 'O começo de tudo', 
              desc: 'Professora e fundadora da Beyond Class ingressa no mundo da educação, assumindo suas primeiras turmas de inglês.', 
              icon: 'ri-book-open-line' 
            },
            { 
              year: '2016', 
              title: 'Vivência de mundo', 
              desc: 'Inicia-se a vivência fora do Brasil. Estudando e trabalhando na Irlanda e Escócia, aprendendo, aprimorando e vivendo o inglês na vida real.', 
              icon: 'ri-global-line' 
            },
            { 
              year: '2024', 
              title: 'Beyond Class nasce', 
              desc: 'Após anos trabalhando em instituições de ensino e aulas individuais, nasce a ideia de abrir uma escola de idiomas. Aqui inicia-se o novo capítulo chamado Beyond Class.', 
              icon: 'ri-rocket-line' 
            },
            { 
              year: '2025', 
              title: 'Primeiro intercâmbio', 
              desc: 'A experiência de estudar e praticar o idioma em situações reais pelas ruas de Londres, Escócia e Irlanda com um grupo de 15 alunos.', 
              icon: 'ri-plane-line' 
            },
            { 
              year: '2026', 
              title: 'Novos horizontes', 
              desc: 'Dois novos grupos prontos para viver novos intercâmbios pelo mundo, novas turmas e professores com vivências internacionais e a Beyond Readers: Um clube de leitura estará nascendo', 
              icon: 'ri-star-line' 
            },
            ].map((item, index) => (
            <FadeInUp
              key={item.year}
              delay={index * 0.1}
              className="flex gap-6 mb-8 last:mb-0"
            >
              <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm">
                <i className={`${item.icon} text-xl text-olive-600`}></i>
              </div>
              {index < 4 && <div className="w-0.5 flex-1 bg-olive-300 mt-4" />}
              </div>
              <div className="flex-1 pb-6">
              <span className="text-olive-700 font-semibold">{item.year}</span>
              <h3 className="text-lg font-semibold text-earth-800 mt-1">{item.title}</h3>
              <p className="text-earth-600 mt-2 leading-relaxed">{item.desc}</p>
              </div>
            </FadeInUp>
            ))}
        </div>
      </Section>

      {/* Values */}
      <Section bgColor="white">
        <FadeInUp className="text-center mb-12">
          <p className="text-olive-600 font-medium mb-2">O que nos guia</p>
          <h2 className="text-3xl md:text-4xl font-bold text-earth-800">
            Nossos Valores
          </h2>
        </FadeInUp>

        <StaggerContainer className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { 
              icon: 'ri-focus-3-line', 
              title: 'Autenticidade', 
              desc: 'Ensinamos o inglês que realmente se fala no mundo. Expressões reais, situações reais, resultados reais.' 
            },
            { 
              icon: 'ri-seedling-line', 
              title: 'Crescimento', 
              desc: 'Cada aluno tem seu próprio ritmo de aprendizado. Respeitamos e incentivamos cada passo do caminho.' 
            },
            { 
              icon: 'ri-team-line', 
              title: 'Conexão', 
              desc: 'Idiomas conectam pessoas e abrem portas. Nosso objetivo é preparar você para essas oportunidades.' 
            },
          ].map((value) => (
            <StaggerItem key={value.title}>
              <Card className="text-center h-full">
                <div className="w-14 h-14 bg-olive-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <i className={`${value.icon} text-2xl text-olive-600`}></i>
                </div>
                <h3 className="text-xl font-semibold text-earth-800 mb-3">
                  {value.title}
                </h3>
                <p className="text-earth-600 leading-relaxed">
                  {value.desc}
                </p>
              </Card>
            </StaggerItem>
          ))}
        </StaggerContainer>
      </Section>

      {/* CTA */}
      <Section bgColor="dark">
        <FadeInUp className="text-center max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-sand-100 mb-4">
            Quer fazer parte dessa história?
          </h2>
          <p className="text-sand-300 text-lg mb-8 leading-relaxed">
            Sua jornada de aprendizado começa com uma conversa. 
            Agende uma aula experimental gratuita e descubra como podemos ajudar você.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              to="/contato" 
              size="lg" 
              className="bg-olive-200 text-green-900 hover:bg-white"
              icon="ri-calendar-line"
            >
              Agendar aula grátis
            </Button>
          </div>
        </FadeInUp>
      </Section>
    </>
  );
}
